import Head from "next/head";
import getConfig from "next/config";
import { useRouter } from "next/router";
import { useGlobalContext } from "../contexts";

type tMetaTag = string;
type tMetaName = string;
type tMetaValue = string;

export interface iTag {
	metaTag: tMetaTag;
	metaName: tMetaName;
	metaValue: tMetaValue;
}

interface iMetaTagsListProps {
	metaTags: iTag[];
	title?: string;
	nid: string;
	domain: string;
}

const MetaTagsList = ({ metaTags = [], title, nid, domain }: iMetaTagsListProps) => {
	const { publicRuntimeConfig } = getConfig();
	const { getPredictionsPageCode } = useGlobalContext();
	const router = useRouter();

	metaTags = metaTags || [];
	const availableTags = ["meta", "base", "link", "style", "script"];
	if (!metaTags.length) {
		return null;
	}

	const tagsFiltered = metaTags
		.filter(({ metaTag, metaName, metaValue }: iTag) => !!metaTag && !!metaValue && !!metaName)
		.filter(({ metaTag }) => availableTags.indexOf(metaTag) > -1);

	let canonicalTag = tagsFiltered?.find(tag => tag.metaTag === "link" && tag.metaName === "canonical");

	if (canonicalTag && Number(getPredictionsPageCode) === Number(nid)) {
		canonicalTag = {
			...canonicalTag,
			metaValue: `${domain.slice(0, -1)}${router.asPath}`
		};
	}

	const filteredMetaTags = metaTags.filter(
		({ metaTag, metaName }) => !(metaTag === "link" && metaName === "canonical")
	);

	const tags: any = [];

	filteredMetaTags.forEach(({ metaTag, metaName, metaValue }: iTag, index: number) => {
		if (metaTag === "meta" && metaName === "title") {
			tags.push(<title key={`title-${index}`}>{metaValue}</title>);
		} else if (metaTag === "meta") {
			if (metaName.includes("og:")) {
				tags.push(<meta key={index} property={metaName} content={metaValue} />);
			} else {
				tags.push(<meta key={index} name={metaName} content={metaValue} />);
			}
		} else if (metaTag === "link") {
			tags.push(<link key={`link-${index}`} rel={metaName} href={metaValue} />);
		} else if (metaTag === "style") {
			tags.push(<style key={index}>{metaValue}</style>);
		} else if (metaTag === "script") {
			tags.push(<script key={index}>{metaValue}</script>);
		}
	});

	if (canonicalTag) {
		const formattedMetaValue = canonicalTag?.metaValue?.replace(/httpss:\/\//g, "https://");
		tags.push(<link key="canonical" rel={canonicalTag?.metaName} href={formattedMetaValue} />);
	}

	return (
		<>
			<Head>
				<link rel="shortcut icon" href={`/${publicRuntimeConfig.subdir}/favicon.ico`} />
				<title>{title || "Page Not Found"}</title>
				<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
				{tags}
			</Head>
		</>
	);
};

export default MetaTagsList;
